import type { DoppeViewerAnalyticsEvents } from "@wix/doppe-common";
import {DoppeViewerAppClientServiceBase} from './doppe-viewer-app-client-service-base';
import type {ConsentPreferences, OmitStrict} from '@wix/devzai-utils-common';
import {
    arrayOrderBy,
    assertNotNullable,
    asyncCachedValueCreate,
    compareStringsCaseInsensitive,
    evaluateFunction,
    objectOmit,
    tsTypeAssert
} from '@wix/devzai-utils-common';
import type {DoppeViewerBiEvents} from '../doppe-viewer-wix-web-bi-logger/doppe-viewer-wix-web-bi-logger';
import {gaClientIdentify, gaClientTrackEvent, gaInitialize, gaUpdateConsent} from '@wix/devzai-utils-dom';
import {
    GoogleAnalyticsEventMap
} from '../../client-server-common/doppe-viewer-google-analytics-events/doppe-viewer-google-analytics-events';
import type {DoppeDtoBymoLinkViewerData} from '../../client-server-common/types/doppe-dto-bymo-link-viewer-data';

type PotentialNoneHashableKeys<EVENT extends DoppeViewerAnalyticsEvents.Events> = Exclude<keyof EVENT, keyof DoppeViewerAnalyticsEvents.EventBase<any> | keyof DoppeViewerAnalyticsEvents.ImplicitParams>;

type NoneHashableParamsNamesMap = Partial<{
    [EVENT in DoppeViewerAnalyticsEvents.Events as EVENT['eventName']]: PotentialNoneHashableKeys<EVENT>[]
}>;

const NoneHashableParamsNamesMap = {
    'uouLockActionViewed': ['lockSource'],
    'uouLockActionUnlocked': ['lockSource'],
    'uouActionStartUnlocking': ['lockSource'],
    'uouActionUnlocked': ['lockSource'],
    'uouActionViewed': ['sourceType'],
    'uouActionImpression': ['sourceType'],
    'uouActionClicked': ['goalType', 'goalSource'],
    'uouActionInnerItemGoalAchieved': ['goalType', 'goalSource'],
    'uouViewedInnerItemGoalAchieved': ['viewSource'],
    'uouActionInnerItemPreviewed': ['sourceType', 'previewType'],
} satisfies NoneHashableParamsNamesMap as NoneHashableParamsNamesMap

export class DoppeViewerAppAnalyticsClient extends DoppeViewerAppClientServiceBase {

    private bymoLinkViewerData: DoppeDtoBymoLinkViewerData.WithConnectedPage | null = null;
    private indexInSessionStore:Record<string,number> = {};
    private isInitialized = false;
    private reportAnalyticsEvents = true;
    private asyncCachedWixBiWebLogger = asyncCachedValueCreate(async () => {
        const {
            doppeViewerWixWebBiLoggerCreate,
            doppeViewerBiEvents
        } = await import (/* webpackChunkName: "doppe-viewer-wix-web-bi-logger" */ '../doppe-viewer-wix-web-bi-logger/doppe-viewer-wix-web-bi-logger');

        return {
            logger: doppeViewerWixWebBiLoggerCreate(),
            doppeViewerBiEvents: doppeViewerBiEvents
        }
    })

    public initialize(
        options: {
            bymoLinkViewerData: DoppeDtoBymoLinkViewerData.WithConnectedPage;
            reportAnalyticsEvents: boolean;
        }
    ) {
        if (this.isInitialized) {
            throw new Error(`DoppeRecorderClientService has already been initialized.`)
        }

        this.isInitialized = true;

        this.bymoLinkViewerData = options.bymoLinkViewerData;
        this.reportAnalyticsEvents = options.reportAnalyticsEvents;

        const consentManager = assertNotNullable(this.clientServices.doppeViewerAppCookieService?.cookieConsentManager);
        const userGASettings = this.clientServices.bymoService.getBymoLinkViewerData()?.userGoogleAnalyticsSettings;

        if (userGASettings?.tagId !== undefined) {
            gaInitialize(userGASettings.tagId, {
                consent: {
                    analyticsStorage: consentManager.hasConsent('anl') ? 'granted' : 'denied',
                }
            });
            
            gaClientIdentify(userGASettings.tagId, this.clientServices.doppeViewerClientData.clientId);
            consentManager.eventEmitter.on('consentChanged', (newConsent:  Partial<ConsentPreferences>) => {
                if (userGASettings.tagId) {
                    gaUpdateConsent({ analyticsStorage: newConsent.anl === 1 ? 'granted' : 'denied' });
                }
            });
        }

        const cookieAccessor = assertNotNullable(this.clientServices.doppeViewerAppCookieService.consentSafeCookieStorageAccessor);
        const userAccountName = assertNotNullable(this.clientServices.bymoService.getBymoLinkViewerData()?.userAccountName);

        return cookieAccessor.updateItem<string>(
            `pagePath`,
            pagePath => pagePath ?? userAccountName
        )
    }

    public flushLogs() {

        if (!this.reportAnalyticsEvents) {
            return;
        }

        const cachedWixBiWebLogger = this.asyncCachedWixBiWebLogger.getCachedValue();
        if (cachedWixBiWebLogger) {
            cachedWixBiWebLogger.logger.flush().catch(() => {

            })
        }
    }

    public logEvent<EVENT_NAME extends DoppeViewerAnalyticsEvents.Events['eventName']>(
        eventName: EVENT_NAME,
        params: OmitStrict<DoppeViewerAnalyticsEvents.EventParams<EVENT_NAME>, keyof DoppeViewerAnalyticsEvents.ImplicitParams>,
    ) {
        if (!this.reportAnalyticsEvents) {
            return;
        }

        const sessionId = this.getSessionId();
        const clientId = this.getClientId();

        const noneHashableParamsNames = NoneHashableParamsNamesMap[eventName];

        const paramsForHashing = noneHashableParamsNames ?
            objectOmit(params, noneHashableParamsNames as any[]) : params;

        const paramsHash = JSON.stringify(arrayOrderBy(Object.entries(paramsForHashing), ([key]) => key, compareStringsCaseInsensitive))

        const uouIndexInSession = this.indexInSessionStore[`indexInSession.${eventName}.${sessionId}.${paramsHash}`] ?? 0;
        this.indexInSessionStore[`indexInSession.${eventName}.${sessionId}.${paramsHash}`] = uouIndexInSession + 1;

        this.apiClient.reportAnalyticsEvent({
            eventName: eventName,
            eventParams: {
                ...params,
                ...tsTypeAssert<DoppeViewerAnalyticsEvents.DoppeServerImplicitParams>({
                    uouIndexInSession: uouIndexInSession,
                })
            } as DoppeViewerAnalyticsEvents.DoppeServerEventParams<EVENT_NAME>
        })
            .catch(() => {
                console.warn(`Failed reporting analytics event '${eventName}' to doppe analytics.`)
            });

        evaluateFunction(async () => {

            const {
                doppeViewerBiEvents,
                logger
            } = await this.asyncCachedWixBiWebLogger.getValue();

            const biEventLogObjectFactory = doppeViewerBiEvents[eventName as DoppeViewerBiEvents];

            if (biEventLogObjectFactory === undefined) {
                console.warn(`Event '${eventName}' isn't available on bi-logger`)
            } else {
                await logger.report(doppeViewerBiEvents[eventName as DoppeViewerBiEvents]?.({
                    ...params,
                    ...tsTypeAssert<DoppeViewerAnalyticsEvents.ImplicitParams>({
                        uouReferrer: document.referrer,
                        uouIndexInSession: uouIndexInSession,
                        uouSessionId: sessionId,
                        uouClientId: clientId,
                        _msid: this.bymoLinkViewerData?.creatorsMetaSiteId ?? null
                    })
                }));
            }
        })
            .catch(() => {
                console.warn(`Failed logging bi event '${eventName}'`)
            });

        try {
            if (GoogleAnalyticsEventMap[eventName]) {
                gaClientTrackEvent(GoogleAnalyticsEventMap[eventName]!.name, {});
            }
        } catch (error) {
            console.warn(`Failed reporting analytics event '${eventName}' to google analytics`)
        }
    }
}